import { useState } from 'react';
import { useConfig } from '../contexts/ConfigContext';
import { useLayout } from '../contexts/LayoutContext';
import { styles } from './CustomerFormStyles';
import CustomerFormContactSection from './CustomerFormContactSection';
import CustomerFormAddressSection from './CustomerFormAddressSection';
import CustomerFormFinancialSection from './CustomerFormFinancialSection';
import CustomerFormHospitalitySection from './CustomerFormHospitalitySection';
import CustomerFormBasicDetails from './CustomerFormBasicDetails';
import CustomerSearchPanel from './customer-form/CustomerSearchPanel';

import type { CustomerFormProps, CustomerFormCustomer, SearchResultCustomer } from './customer-form/types';
import { useCustomerFormData } from './customer-form/useCustomerFormData';
import { useCustomerSearch } from './customer-form/useCustomerSearch';
import { useCustomerSave } from './customer-form/useCustomerSave';

/**
 * CustomerForm - Unified Customer Management Component
 *
 * Reusable for Service Log workflow, Customer Management page, and Peace of Sleep enrollment.
 * Email-based duplicate checking, collapsible sections, mobile-first design.
 */
function CustomerForm({
  supabase,
  onSave,
  onCancel,
  initialData = null,
  showPeaceOfSleep = false,
  mode = 'inline',
  globalSearchMode: _globalSearchMode = false,
}: CustomerFormProps) {
  const { countryName } = useConfig();
  const defaultCountry = countryName || 'South Africa';
  const { isMobile } = useLayout();

  // UI State — collapsible sections
  const [expandedSections, setExpandedSections] = useState({
    contact: false,
    address: false,
    financial: false,
    hospitality: false,
  });

  function toggleSection(section: string) {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section as keyof typeof prev],
    }));
  }

  // Form data hook
  const {
    formData, setFormData,
    selectedCustomer, setSelectedCustomer,
    displayNameManual, setDisplayNameManual,
    originalContactPersons,
    customerProperties, loadingProperties,
    loadCustomerData, clearSelection,
  } = useCustomerFormData({ supabase, initialData, defaultCountry, expandedSections });

  // Search hook
  const {
    searchTerm, setSearchTerm,
    searchResults, searching, showSearchResults,
    duplicateCustomer, checkingDuplicate,
    successMessage,
    handleSelectCustomer, handleRestoreCustomer, handleClearSelection,
  } = useCustomerSearch({
    supabase,
    selectedCustomer,
    onCustomerLoaded: (fullCustomer) => {
      // If the loaded customer has no billing address but initialData does (e.g. from booking lookup),
      // merge the booking's address in so it isn't lost when the duplicate is loaded.
      const needsAddress = !fullCustomer.billing_address?.address;
      const hasInitialAddress = initialData?.billing_address?.address;
      const customerToLoad = (needsAddress && hasInitialAddress)
        ? { ...fullCustomer, billing_address: { ...fullCustomer.billing_address, ...initialData.billing_address } }
        : fullCustomer;
      loadCustomerData(customerToLoad);
      setSelectedCustomer(fullCustomer);
      setDisplayNameManual(true);
    },
    onClearSelection: clearSelection,
    formEmail: formData.email,
    formCompanyName: formData.company_name,
  });

  // Save hook
  const { saving, error, setError, handleSave } = useCustomerSave({
    supabase, formData, selectedCustomer, duplicateCustomer,
    originalContactPersons, defaultCountry, onSave, toggleSection,
  });

  // Form handlers
  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) {
    const { name, value, type } = e.target;
    const checked = 'checked' in e.target ? e.target.checked : false;
    if (name === 'customer_name') setDisplayNameManual(true);
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setError(null);
  }

  function handleAddressChange(addressType: string, field: string, value: string) {
    setFormData(prev => ({
      ...prev,
      [addressType]: {
        ...(prev[addressType as keyof typeof prev] as unknown as Record<string, string>),
        [field]: value,
      },
    }));
  }

  // Search panel callback — bridges async handleSelectCustomer to error state
  function onSelectFromSearch(customer: SearchResultCustomer | CustomerFormCustomer) {
    handleSelectCustomer(customer).then(err => { if (err) setError(err); });
  }

  // Restore a deleted customer, then load them in. Same bridge as above.
  function onRestoreFromDuplicate(customer: CustomerFormCustomer) {
    handleRestoreCustomer(customer).then(err => { if (err) setError(err); });
  }

  // Render
  const isEditing = !!selectedCustomer;

  const content = (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>
          {isEditing ? `Edit: ${selectedCustomer.customer_name}` : 'Customer Details'}
        </h2>
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}
      {successMessage && <div style={styles.successBox}>{successMessage}</div>}

      <CustomerSearchPanel
        selectedCustomer={selectedCustomer}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        searching={searching}
        showSearchResults={showSearchResults}
        searchResults={searchResults}
        duplicateCustomer={duplicateCustomer}
        onSelectCustomer={onSelectFromSearch}
        onRestoreCustomer={onRestoreFromDuplicate}
        onClearSelection={handleClearSelection}
        styles={styles}
      />

      <CustomerFormBasicDetails
        formData={formData}
        handleChange={handleChange}
        setFormData={setFormData}
        setDisplayNameManual={setDisplayNameManual}
        displayNameManual={displayNameManual}
        duplicateCustomer={duplicateCustomer}
        selectedCustomer={selectedCustomer}
        checkingDuplicate={checkingDuplicate}
        styles={styles}
      />

      <CustomerFormContactSection
        expanded={expandedSections.contact}
        onToggle={() => toggleSection('contact')}
        formData={formData}
        onChange={handleChange}
        styles={styles}
      />

      <CustomerFormAddressSection
        expanded={expandedSections.address}
        onToggle={() => toggleSection('address')}
        formData={formData}
        onChange={handleChange}
        onAddressChange={handleAddressChange}
        styles={styles}
      />

      <CustomerFormFinancialSection
        expanded={expandedSections.financial}
        onToggle={() => toggleSection('financial')}
        formData={formData}
        onChange={handleChange}
        setFormData={(updater) => setFormData(prev => updater(prev) as typeof prev)}
        setDisplayNameManual={setDisplayNameManual}
        styles={styles}
      />

      {showPeaceOfSleep && (
        <CustomerFormHospitalitySection
          expanded={expandedSections.hospitality}
          onToggle={() => toggleSection('hospitality')}
          formData={formData}
          onChange={handleChange}
          selectedCustomer={selectedCustomer}
          customerProperties={customerProperties}
          loadingProperties={loadingProperties}
          styles={styles}
        />
      )}

      <div style={styles.footer}>
        <div style={styles.footerNote}>
          {isEditing ? 'Changes saved to database' : 'Customer saved to database'}
        </div>
        <div style={styles.footerButtons}>
          {onCancel && (
            <button type="button" style={styles.cancelBtn} onClick={onCancel} disabled={saving}>
              Cancel
            </button>
          )}
          <button type="button" style={styles.saveBtn} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : isEditing ? 'Update' : 'Save & Verify'}
          </button>
        </div>
      </div>
    </div>
  );

  if (mode === 'modal') {
    const modalStyle = isMobile
      ? { ...styles.modal, maxWidth: '100%', padding: '0.75rem', paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }
      : styles.modal;
    return (
      <div style={styles.overlay} onClick={onCancel}>
        <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
          {content}
        </div>
      </div>
    );
  }

  return content;
}

export default CustomerForm;
